import * as React from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogPrimaryAction from "../components/DialogPrimaryAction";

const noopGuard = {
	arm: () => {},
	disarm: () => {},
	requestNavigation: (action) => action(),
};

const NavigationGuardContext = React.createContext(noopGuard);

// Globalna blokada nawigacji - używana przez trwający egzamin (ExamTrainer), żeby
// zapytać o potwierdzenie zanim użytkownik zejdzie z ekranu i bezpowrotnie porzuci
// wynik. Zamontowana raz w App.jsx, więc przeżywa odmontowanie strony, na której
// blokadę włączono - dzięki temu łapie też przycisk "Wstecz" przeglądarki.
export function NavigationGuardProvider({ children }) {
	const armedRef = React.useRef(false);
	const guardedPathRef = React.useRef(null);
	const onConfirmLeaveRef = React.useRef(null);
	const pendingActionRef = React.useRef(null);
	const [dialogOpen, setDialogOpen] = React.useState(false);
	const navigate = useNavigate();

	const arm = React.useCallback((path, onConfirmLeave) => {
		armedRef.current = true;
		guardedPathRef.current = path;
		onConfirmLeaveRef.current = onConfirmLeave ?? null;
	}, []);

	const disarm = React.useCallback(() => {
		armedRef.current = false;
		guardedPathRef.current = null;
		onConfirmLeaveRef.current = null;
	}, []);

	const requestNavigation = React.useCallback((action) => {
		if (!armedRef.current) {
			action();
			return;
		}
		pendingActionRef.current = action;
		setDialogOpen(true);
	}, []);

	React.useEffect(() => {
		function handleBeforeUnload(event) {
			if (!armedRef.current) return;
			event.preventDefault();
			event.returnValue = "";
		}
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, []);

	// "Wstecz" w przeglądarce zdąży już zmienić adres zanim się o tym dowiemy -
	// popstate nie da się anulować. Dlatego pytamy o potwierdzenie post factum;
	// przy odmowie programowo wracamy na zapamiętaną ścieżkę egzaminu.
	React.useEffect(() => {
		function handlePopState() {
			if (!armedRef.current) return;
			pendingActionRef.current = null;
			setDialogOpen(true);
		}
		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
	}, []);

	function handleConfirm() {
		const action = pendingActionRef.current;
		onConfirmLeaveRef.current?.();
		disarm();
		setDialogOpen(false);
		pendingActionRef.current = null;
		action?.();
	}

	function handleCancel() {
		const path = guardedPathRef.current;
		setDialogOpen(false);
		pendingActionRef.current = null;
		if (path && window.location.pathname !== path) {
			navigate(path, { replace: true });
		}
	}

	const value = React.useMemo(
		() => ({ arm, disarm, requestNavigation }),
		[arm, disarm, requestNavigation],
	);

	return (
		<NavigationGuardContext.Provider value={value}>
			{children}
			<Dialog open={dialogOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
				<DialogTitle>Przerwać egzamin?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Egzamin zostanie natychmiast zakończony i nie będzie można do niego wrócić.
					</DialogContentText>
				</DialogContent>
				<DialogPrimaryAction color="error" onClick={handleConfirm}>
					Przerwij egzamin
				</DialogPrimaryAction>
			</Dialog>
		</NavigationGuardContext.Provider>
	);
}

export function useNavigationGuard() {
	return React.useContext(NavigationGuardContext);
}

// Zamiennik useNavigate() dla miejsc, z których można zejść z ekranu trwającego
// egzaminu (menu boczne, breadcrumby, itd.) - gdy blokada jest aktywna, przed
// nawigacją pyta o potwierdzenie zamiast przechodzić od razu.
export function useGuardedNavigate() {
	const navigate = useNavigate();
	const { requestNavigation } = useNavigationGuard();
	return React.useCallback(
		(to, options) => requestNavigation(() => navigate(to, options)),
		[navigate, requestNavigation],
	);
}
